import * as THREE from 'three';
import SceneHelpers from '@vfdp-customs/customs-scene-helpers';

const plugin = ({ $store, $eventBus, $recipe, $model3D }) => {
  return {
    install(Vue) {
      Vue.prototype.$personalization = {
        globalSceneInstance: new SceneHelpers({}).sceneHelpers,
        /**
         * @function createCanvas
         * @param {Number} width - Canvas element width in px
         * @param {Number} height - Canvas element height in px
         * @returns {HTMLCanvasElement}
         * Creates the canvas element to hold the text to be applied to the model.
         */
        createCanvas({ width, height }) {
          const canvas2 = document.createElement("canvas");
          canvas2.width = width;
          canvas2.height = height;
          return canvas2;
        },

        /**
         * @function prepareContext
         * @param {HTMLCanvasElement} canvas2
         * @param {Object} personalizationSettings
         * @return {HTMLCanvasElement}
         * Prepates the context for the canvas.
         */
        prepareContext({ canvas, personalizationSettings}) {
          const context = canvas.getContext("2d");
          const alpha = 1.0;
          context.fillStyle = "rgba(0, 0, 0, " + alpha + ")";
          context.textAlign = personalizationSettings.textAlign;
          context.font =
            personalizationSettings.font_options +
            " " +
            personalizationSettings.fontSize +
            " " +
            personalizationSettings.font;

          context.textBaseline = 'middle';
          context.fillStyle = "black";
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.fillStyle = "white";
          return context;
        },

        /**
         * @function removeTextObjects
         * Removes any personalization previously applied.
         */
        removeTextObjects() {
          const obj = this.globalSceneInstance.getSceneObjectByName({ objectName: $store.state.sceneSetup.loadedModelName });
          obj.children.forEach(item => {
            if (item.name == "Textobj") {
              obj.remove(item);
            }
          });
        },

        /**
         * @function createGrid
         * @param {RenderingContext} context
         * @return {HTMLCanvasElement}
         * Creates a grid in the canvas context to center the text in.
         * Function is only used to find the correct value in the PDM.
         * Function is for local use only.
         */
        createGrid({ context }) {
          if (!context) {
            return;
          }
          let str;
          for (let idx = -1000; idx <= 1000; idx += 50) {
            // TEST Y value - Comment out this section when testing the X value
            str =
              "" +
              idx +
              idx +
              idx +
              idx +
              idx +
              idx +
              idx +
              idx +
              idx +
              idx +
              idx;
            str = str + str + str + str + str + str + str + str + str;
            context.fillText(str, 0, idx);

            // TEST X value - Comment out this section when testing the Y value
          }

          return context;
        },

        /**
         * @function calculateXCoordOffSet
         * @param {Array} config
         * @param {Number} coeficient
         * @param {Object} personalizationSettings
         * @returns {Number}
         * Calculates the coordinates off set for the text in teh canvas using the canvas configuration and the coeficient to offset as arguments
         */
        calculateXCoordOffSet({ config, coeficient, personalizationSettings }) {
          if (coeficient > 0) {
            const fontSize = parseInt(
              personalizationSettings.fontSize.replace("px", ""),
              10
            );
            let textOffset = 0.5;
            if (coeficient === 1) {
              return 0;
            } else if (config && config[coeficient - 1]) {
              textOffset = config[coeficient - 1];
            }
            return 0.5 * coeficient * fontSize * textOffset;
          } else {
            throw new Error(
              "Invalid coeficient argument. Number must be higher that 0."
            );
          }
        },

        /**
         * @function applyTextToCanvas
         * @param {Boolean} debug - Debug rendering
         * @param {String} personalizationComponent - Component name
         * Add the personalization text to the canvas element.
         */
        applyTextToCanvas({ personalizationComponent, leftShoe = false, snapShotModel = null }, callback = () => {}) {
          let text,
            positioning,
            location,
            personalizationSettings;
          const itemsData = JSON.parse(
            JSON.stringify($store.getters["personalization/getItemsData"])
          );
          const ugcTempRecipe = $store.getters["ugc/tempData/getRecipe"];
          const recipe = ugcTempRecipe.components && !$store.state.utils.isDesignTransfering
            ? ugcTempRecipe
            : $store.state.recipe.recipe_v2;

          itemsData.forEach((item) => {
            if (leftShoe !== !!item.apply_to_3D) {
              positioning = item.positioning;
              location = item.location;
            }
          });

          recipe.components[personalizationComponent].personalization.forEach(
            componentData => {
              if (location === componentData.location) {
                text = componentData.text;
                personalizationSettings = componentData;
              }
            }
          );

          if (!text) {
            // Handle the use case when personalization is applied to left shoe only
            if ($store.state.personalization.recipeData.length) {
              this.savePersonalizationInRecipe({ personalizationComponent });
            }

            this.remove3DObjectPersonalization();
            this.setPrice();
            $store.dispatch("swatches/GET_RELATION_SWATCH_BY_FEATURE_ID", "personalization").then((response)=>{
              $store.commit("swatches/SET_DISABLED_SWATCHES_BY_FEATURE", {swatches: response.swatches_not_allowed, feature: "personalization"});
              callback();
              $eventBus.$emit("close-personalization-modal", true);
            });
            return;
          }

          const modelConfig = positioning;
          modelConfig.canvas = this.createCanvas({
            width: personalizationSettings.clone_dimensions[0],
            height: personalizationSettings.clone_dimensions[1]
          });
          modelConfig.context = this.prepareContext({
            canvas: modelConfig.canvas,
            personalizationSettings
          });
          modelConfig.context.fillText(text, modelConfig.canvasX, modelConfig.canvasY);

          this.loadPersonalizationGeo($store.state.sceneSetup.modelBySize).then(geometry =>{
            this.setPersonalizationGeo(geometry, snapShotModel);
            this.applyPersonalizationTo3DModel({
              options: modelConfig,
              personalizationComponent,
              hexcolor: personalizationSettings.hex,
              snapShotModel
            }, callback);
            $eventBus.$emit("remove-components", personalizationComponent);
          });
        },

        applyPersonalizationTo3DModel({ options, personalizationComponent, hexcolor, snapShotModel}, callback = () => {}) {
          const mesh = $store.state.utils.pdm.components.personalization.mesh_material;
          const textcanvas = new THREE.TextureLoader().load(options.canvas.toDataURL());
          const personalization = snapShotModel ?
            snapShotModel.getObjectByName('personalization') : 
            this.globalSceneInstance.getSceneObjectByName({ objectName: 'personalization' });

          // commenting normal map as its not working correctly, will fix in future ticket
          // const normalMap = new THREE.TextureLoader().load(`${$store.state.endPoints.customizer.s3SwatchesBucket}assets${mesh.textures.normalMap.image_url}`);
          // normalMap.flipY = false;
          // normalMap.repeat.set(mesh.textures.normalMap.options.repeat[0], mesh.textures.normalMap.options.repeat[1]);

          textcanvas.flipY = false;

          personalization.visible = true;
          personalization.material.color = new THREE.Color(hexcolor);
          personalization.material.roughness = 1;
          personalization.material.metalness = 0;
          personalization.material.emissive = new THREE.Color(mesh.mesh_options.emissive);
          personalization.material.alphaMap = textcanvas;
          //personalization.material.normalMap = normalMap;
          personalization.material.transparent = true;
          personalization.renderOrder = 999;
          personalization.material.normalScale.set(mesh.mesh_options.normalScale[0], mesh.mesh_options.normalScale[1]);

          //Commenting normal map setting as it is not working correctly as mention above.
          //this.globalSceneInstance.setUniform({material: personalization.material, uniformName: "normalRepeat", value: 15});
          this.setPrice();
          $store.commit("personalization/ADD_PERSONALIZATION_COMPONENT", personalizationComponent);
          this.removeDisallowedComponents(personalizationComponent, true);
          this.savePersonalizationInRecipe({ personalizationComponent });
          $store.dispatch("swatches/GET_RELATION_SWATCH_BY_FEATURE_ID", "personalization").then((response)=>{
            $store.commit("swatches/SET_DISABLED_SWATCHES_BY_FEATURE", { swatches: response.swatches_not_allowed, feature: "personalization" });
            callback();
            if ($store.state.utils.configuration.enableCollapsingSwatches) {
              $eventBus.$emit("loadSwatches");
            }
            $eventBus.$emit("close-personalization-modal", true);
          })

        },

        savePersonalizationInRecipe({ personalizationComponent }) {
          const personalizationTempRecipeData = JSON.parse(JSON.stringify($store.state.personalization.recipeData));
          let data = $store.state.recipe.recipe_v2.components[personalizationComponent];
          if(personalizationTempRecipeData.length > 0 && data) {
            data.personalization = [...personalizationTempRecipeData];
            $store.commit("recipe/UPDATE_COMPONENT", { componentName: personalizationComponent, data });
            $store.commit("recipe/SAVE_PERSONALIZATION", { componentName: personalizationComponent, personalization: personalizationTempRecipeData });
          }
        },

        /**
         * @function setPrice
         * Changes the price to include the personalization upcharge.
         */
        setPrice() {
          $store.dispatch("buyPanel/GET_PRICE", { recipe: $store.state.recipe.recipe_v2 });
        },

        /**
         * @function applyPersonalizationRecipe
         * @param {String} componentName
         * Apply a recipe with personalization.
         */
        applyPersonalizationRecipe({ componentName }) {
          this.applyTextToCanvas({ personalizationComponent: componentName });
        },

        /**
         * @function removeDisallowedComponents
         * @param {String} componentName - Component to which personalization will be applied
         * Removes disallowed components from temp recipe according to PDM data
         */
        removeDisallowedComponents(componentName, applyingTo3Dmodel) {
          const pdm = $store.state.utils.pdm;
          const tmpRecipe = $store.getters["ugc/tempData/getRecipe"];
          const componentsToRemove = pdm.components.personalization.personalization_remove_components;
          if (componentsToRemove && componentsToRemove.length) {
            componentsToRemove.forEach(component => {
              if(applyingTo3Dmodel) {
                const recipe = $store.state.recipe.recipe_v2.components;
                const selectedObject = $model3D.sceneHelpers.getSceneObjectByName({ objectName: component });

                if(recipe[component]) {
                  $store.commit('geometryUpdates/SET_UPGRADE_OPTIONS', {
                    swatchId: recipe[component].swatch_id,
                    status: false
                  });
                  $store.commit("recipe/REMOVE_COMPONENT", component);
                }

                if (selectedObject) {
                  $model3D.modelObj.remove(selectedObject);
                  $model3D.sceneHelpers.disposeMeshObject({ meshObject: selectedObject });
                }
              }
              delete tmpRecipe.components[component];
            });
            $store.commit("ugc/tempData/SET_RECIPE", tmpRecipe);
          }

        },
        removePersonalization() {
          const componentName = $store.getters["personalization/getPersonalizationComponentName"];
          this.remove3DObjectPersonalization();
          $store.commit("personalization/RESET_STEP");
          $store.commit("personalization/RESET_DATA");
          $store.commit("personalization/CHANGE_VIEW", "personalization-main-view");
          $store.commit("recipe/REMOVE_PERSONALIZATION", componentName);
          $store.commit("swatches/SET_DISABLED_SWATCHES_BY_FEATURE", {swatches: [], feature: "personalization"});
          this.setPrice();
        },
        /**
         * @function loadPersonalizationGeo
         * @param {String} size - Bucket size (9.0)
         * Set personalization geometry in 3D model.
         */
        loadPersonalizationGeo(){
          const pdm = $store.state.utils.pdm;
          const { s3ModelsBucket } = $store.state.endPoints.customizer;
          const bucketURL = s3ModelsBucket;
          const sizeToLoad = $store.state.sceneSetup.modelBySize;
          const modelSKU = pdm.parent_model ? pdm.parent_model : pdm.sku;
          const geoUrl = $store.getters["utils/addCacheBusting"](`${bucketURL}gltf_models/${modelSKU}/${sizeToLoad}/personalization.glb`);

          this.remove3DObjectPersonalization();

          return new Promise((resolve, reject) => {
            $model3D.loadObject(geoUrl).then(geometry => {
              resolve(geometry.children[0].children[0])
            }).catch(reject);
          });
        },
        /**
         * @function setPersonalizationGeo
         * @param {String} componentName
         * Set personalization geometry in 3D model.
         */
        setPersonalizationGeo(geo,snapShotModel) {
          geo.name = "personalization";
          const model3d = snapShotModel || this.globalSceneInstance.model;
          model3d.add(geo);
          if ($store.state.utils.configuration.enableCollapsingSwatches) {
            $eventBus.$emit("loadSwatches");
          }
        },
        remove3DObjectPersonalization(){
          if($model3D.modelObj.getObjectByName("personalization")) {
              $model3D.sceneHelpers.removeSceneObjectByName('personalization');
          }
        }
      };
    }
  };
};
export default plugin;
